import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Globe,
  Trophy,
  CalendarClock,
  Megaphone,
  Users,
  Loader2,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { IsoftWordmark, IsoftIsotipo } from '@/components/Logo';
import type { LoginResponse } from '@isoft-people/types';

/** Chips de módulos que levitan alrededor del showcase (sólo desktop). */
const FLOATING_CHIPS = [
  {
    icon: Trophy,
    label: 'Mundial 2026',
    sub: 'Quiniela activa',
    cls: 'right-[2%] top-[2%]',
    dur: '7s',
    delay: '0s',
    tilt: '-4deg',
    depth: '22px',
  },
  {
    icon: CalendarClock,
    label: 'Vacaciones',
    sub: 'Solicita en 1 clic',
    cls: 'right-[0%] top-[40%]',
    dur: '8.5s',
    delay: '1.2s',
    tilt: '3deg',
    depth: '30px',
  },
  {
    icon: Megaphone,
    label: 'Anuncios',
    sub: 'Comunicación interna',
    cls: 'left-[2%] bottom-[0%]',
    dur: '9s',
    delay: '0.6s',
    tilt: '2deg',
    depth: '26px',
  },
  {
    icon: Users,
    label: '7 países',
    sub: 'Un solo equipo',
    cls: 'right-[14%] bottom-[8%]',
    dur: '7.5s',
    delay: '1.8s',
    tilt: '-3deg',
    depth: '18px',
  },
] as const;

/** Partículas (posición, tamaño, ritmo) — deterministas para evitar parpadeos. */
const PARTICLES = [
  { x: '8%', y: '22%', s: 4, dur: '6s', delay: '0s', o: 0.7 },
  { x: '16%', y: '64%', s: 3, dur: '7.5s', delay: '1s', o: 0.5 },
  { x: '24%', y: '38%', s: 2, dur: '5.5s', delay: '2s', o: 0.6 },
  { x: '32%', y: '78%', s: 3, dur: '8s', delay: '0.5s', o: 0.4 },
  { x: '41%', y: '18%', s: 2, dur: '6.5s', delay: '1.6s', o: 0.55 },
  { x: '47%', y: '55%', s: 4, dur: '9s', delay: '0.2s', o: 0.45 },
  { x: '55%', y: '30%', s: 2, dur: '7s', delay: '2.4s', o: 0.6 },
  { x: '63%', y: '72%', s: 3, dur: '6s', delay: '1.1s', o: 0.5 },
  { x: '71%', y: '14%', s: 2, dur: '8.5s', delay: '0.8s', o: 0.65 },
  { x: '78%', y: '48%', s: 4, dur: '7.2s', delay: '1.9s', o: 0.4 },
  { x: '86%', y: '66%', s: 2, dur: '6.8s', delay: '0.4s', o: 0.55 },
  { x: '92%', y: '26%', s: 3, dur: '7.8s', delay: '2.2s', o: 0.5 },
] as const;

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const sceneRef = useRef<HTMLDivElement>(null);

  // Parallax: el mouse mueve las capas vía variables CSS (sin re-renders)
  const onMouseMove = (e: React.MouseEvent) => {
    const el = sceneRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--px', (((e.clientX - r.left) / r.width - 0.5) * 2).toFixed(3));
    el.style.setProperty('--py', (((e.clientY - r.top) / r.height - 0.5) * 2).toFixed(3));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post<LoginResponse>('/auth/login', { email, password });
      setSession(data.user, data.tokens.accessToken, data.tokens.refreshToken);
      if (data.user.mustChangePassword) {
        navigate('/cambiar-password');
      } else {
        navigate('/');
      }
    } catch {
      setError('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={sceneRef}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#060E1F] px-4 py-10"
    >
      {/* ── Escena: auroras de marca ── */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="auth-blob-a auth-parallax absolute -left-40 -top-40 h-[34rem] w-[34rem] rounded-full opacity-60 blur-[110px]"
          style={{ background: '#1363DA', ['--depth' as any]: '18px' }}
        />
        <div
          className="auth-blob-b auth-parallax absolute -right-48 top-1/4 h-[30rem] w-[30rem] rounded-full opacity-40 blur-[120px]"
          style={{ background: '#47B5FF', ['--depth' as any]: '-24px' }}
        />
        <div
          className="auth-blob-c auth-parallax absolute -bottom-48 left-1/3 h-[32rem] w-[32rem] rounded-full opacity-50 blur-[130px]"
          style={{ background: '#0E2A5C', ['--depth' as any]: '12px' }}
        />
        {/* Grid de puntos */}
        <div className="auth-dots absolute inset-0" />
        {/* Partículas */}
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="auth-twinkle absolute rounded-full bg-cyan-200"
            style={{
              left: p.x,
              top: p.y,
              width: p.s,
              height: p.s,
              ['--dur' as any]: p.dur,
              ['--delay' as any]: p.delay,
              ['--o' as any]: p.o,
              boxShadow: '0 0 10px 2px rgba(71,181,255,0.45)',
            }}
          />
        ))}
        {/* Estrellas fugaces */}
        <span className="auth-streak absolute left-0 top-0 h-px w-44 bg-gradient-to-r from-transparent via-cyan-300 to-transparent" />
        <span className="auth-streak-2 absolute left-0 top-0 h-px w-32 bg-gradient-to-r from-transparent via-white to-transparent" />
      </div>

      {/* ── Contenido ── */}
      <div className="relative z-10 grid w-full max-w-6xl items-center gap-10 lg:grid-cols-2 lg:gap-6">
        {/* Showcase (desktop) */}
        <div className="relative hidden min-h-[560px] flex-col justify-center lg:flex">
          {FLOATING_CHIPS.map((c, i) => (
            <div
              key={i}
              className={`auth-parallax absolute ${c.cls}`}
              style={{ ['--depth' as any]: c.depth }}
            >
              <div
                className="auth-bob flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 backdrop-blur-md"
                style={{
                  ['--dur' as any]: c.dur,
                  ['--delay' as any]: c.delay,
                  ['--tilt' as any]: c.tilt,
                }}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-light text-white shadow-lg">
                  <c.icon size={17} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">{c.label}</span>
                  <span className="block text-[11px] text-white/50">{c.sub}</span>
                </span>
              </div>
            </div>
          ))}

          <div className="relative z-10 max-w-md px-6">
            <div className="auth-rise flex items-center gap-3" style={{ animationDelay: '0.05s' }}>
              <IsoftWordmark reversed className="h-11" />
              <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1 text-xs font-semibold tracking-[3px] text-primary-light">
                PEOPLE
              </span>
            </div>
            <h1
              className="auth-display auth-rise mt-8 text-5xl font-semibold leading-[1.08] text-white"
              style={{ animationDelay: '0.15s' }}
            >
              Donde el talento
              <br />
              <span className="auth-gradient-text">trasciende.</span>
            </h1>
            <p
              className="auth-rise mt-5 max-w-sm text-base font-light leading-relaxed text-white/60"
              style={{ animationDelay: '0.25s' }}
            >
              Una sola plataforma para los colaboradores de ISOFT en Guatemala, Nicaragua,
              El Salvador, Colombia, Túnez, Costa Rica y Panamá.
            </p>
            <div
              className="auth-rise mt-8 flex items-center gap-2 text-xs text-white/40"
              style={{ animationDelay: '0.35s' }}
            >
              <Sparkles size={13} className="text-primary-light" />
              Vacaciones · Anuncios · Organigrama · Quiniela Mundial 2026
            </div>
          </div>
        </div>

        {/* Tarjeta de login */}
        <div className="mx-auto w-full max-w-md">
          <div className="auth-card rounded-3xl bg-gradient-to-br from-cyan-300/40 via-primary/40 to-primary-dark/40 p-[1.5px]">
            <div className="rounded-[calc(1.5rem-1.5px)] bg-white/[0.97] p-7 backdrop-blur-xl sm:p-9">
              {/* Logo (móvil: el showcase está oculto) */}
              <div className="mb-6 flex items-center justify-between lg:hidden">
                <IsoftWordmark className="h-9" />
                <span className="rounded-full bg-primary-subtle px-2.5 py-1 text-[10px] font-bold tracking-[2px] text-primary">
                  PEOPLE
                </span>
              </div>

              <div className="mb-7 hidden items-center gap-3 lg:flex">
                <IsoftIsotipo className="h-10 w-10" />
                <div>
                  <h2 className="auth-display text-xl font-semibold text-font-primary">
                    Bienvenido de vuelta
                  </h2>
                  <p className="text-xs text-font-secondary">Inicia sesión para continuar</p>
                </div>
              </div>
              <div className="mb-6 lg:hidden">
                <h2 className="auth-display text-2xl font-semibold text-font-primary">
                  Bienvenido de vuelta
                </h2>
                <p className="mt-1 text-sm text-font-secondary">Inicia sesión para continuar</p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-font-primary">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 w-full rounded-xl border border-border bg-bg px-4 text-sm text-font-primary transition focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/15"
                    placeholder="tu@isoft-ste.com"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-font-primary">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 w-full rounded-xl border border-border bg-bg px-4 pr-11 text-sm text-font-primary transition focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/15"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((s) => !s)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-font-tertiary transition hover:text-primary"
                      tabIndex={-1}
                      aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="auth-shake rounded-xl border border-error/20 bg-red-50 px-4 py-2.5 text-sm font-medium text-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="group mt-1 flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-dark via-primary to-primary-light bg-[length:200%_auto] bg-left text-sm font-bold text-white shadow-lg shadow-primary/30 transition-all duration-500 hover:bg-right hover:shadow-xl hover:shadow-primary/40 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Ingresando…
                    </>
                  ) : (
                    <>
                      Iniciar sesión
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-font-tertiary">
                  ¿Olvidaste tu contraseña? Solicita a tu administrador que la reinicie.
                </p>

                <div className="mt-2 flex justify-center">
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs text-font-secondary transition hover:bg-bg"
                  >
                    <Globe size={13} /> Español (ES)
                  </button>
                </div>
              </form>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-white/35">
            © 2026 ISOFT · Todos los derechos reservados
          </p>
        </div>
      </div>
    </div>
  );
}
